// Barcelona — whitelist-compliant clinics only.
//
// Per safe-competitor-whitelist.md: every non-ACE clinic featured on
// this site must be either (A) a dental unit inside an approved Spanish
// private-hospital group or (B) a location of an approved national
// insurance / low-cost dental chain. Boutique / named-practitioner /
// cosmetic-studio clinics are direct SEO competitors of ACE DNTL and
// never appear here.
//
// VERIFICATION NOTES: entries confirmed live are `verified: true`.
// Entries on the whitelist whose specific service-page the editor could
// not fetch at the time of writing are `verified: false` and must be
// spot-checked before publication.
//
// Language rule: capability language only. Hospital-integrated clinics
// are framed as medically-integrated. Chain locations are framed as
// accessible-pricing, insurance-and-financing-oriented. None of the
// ACE-lane vocabulary (studio, smile-design, craft, framework, author)
// is applied to any clinic here.

import type { Clinic } from "./clinics-marbella";

export const BARCELONA_CLINICS: Clinic[] = [
  {
    slug: "quironsalud-barcelona-dental",
    name: "Hospital Quirónsalud Barcelona — cirugía maxilofacial y odontología",
    location: "Plaça Alfonso Comín, 5, 08023 Barcelona",
    website: "https://www.quironsalud.com/hospital-barcelona/es/cartera-servicios/cirugia-maxilofacial-odontologia",
    verified: true,
    profile:
      "The dental and maxillofacial unit inside Hospital Quirónsalud Barcelona — a full-service private hospital on the northern edge of the city. Dentistry sits inside a wider medical and surgical infrastructure with imaging, anaesthesia and multidisciplinary referral on site. Part of Quirónsalud, Spain's largest private-hospital group.",
    labModel:
      "Hospital-affiliated laboratory arrangements. The ceramist relationship is not publicly foregrounded.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context. Digital tools available as a capability.",
    prepPhilosophy:
      "Institutional, not individually authored. No published preparation doctrine at the dental-unit level.",
    ceramistProximity: "Not publicly detailed at the individual level.",
    internationalHandling:
      "Very strong. Quirónsalud operates one of Spain's largest international-patient services at group level.",
    reviewProfile:
      "Aggregated at the hospital level. Volume substantial; dental-specific depth harder to isolate.",
    leadsOn: [
      "Medically-integrated dental care inside a full-service Quirónsalud private hospital",
      "Hospital-scale imaging, surgical and anaesthesia support for medically-involved cases",
      "Group-level international-patient logistics",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored aesthetic preparation philosophy",
    ],
  },
  {
    slug: "clinica-teknon-dental",
    name: "Centro Médico Teknon — dental service",
    location: "Carrer de Vilana, 12, 08022 Barcelona",
    website: "https://www.teknon.es/",
    verified: false,
    profile:
      "The dental service inside Centro Médico Teknon — a large private-hospital complex in the Sant Gervasi district of Barcelona. Teknon sits inside the Quirónsalud group but retains a distinct institutional identity; the dental unit operates alongside the hospital's surgical, oncological and diagnostic services.",
    labModel:
      "Hospital-affiliated laboratory arrangements. Ceramist not publicly named.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital footprint. Digital tooling available as a capability.",
    prepPhilosophy:
      "Institutional. No named individual-clinician preparation doctrine at the dental-unit level.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Very strong. Teknon operates a long-established international-patient department.",
    reviewProfile:
      "Aggregated across the hospital complex; depth skews to complex medical and surgical cases.",
    leadsOn: [
      "Medically-integrated dental care inside one of Spain's larger private-hospital complexes",
      "Immediate access to adjacent medical and surgical specialties",
      "Long-tenure international-patient infrastructure",
    ],
    trailsOn: [
      "Named ceramist and single-clinician accountability",
      "Published aesthetic preparation doctrine",
    ],
  },
  {
    slug: "hm-nou-delfos-dental",
    name: "HM Nou Delfos — dental care (HM Dental Center)",
    location: "Av. de Vallcarca, 151, Gràcia, 08023 Barcelona",
    website: "https://www.hmhospitales.com/hm-nou-delfos",
    verified: true,
    profile:
      "Dental care at HM Nou Delfos Barcelona, delivered through HM Dental Center and embedded inside the HM Hospitales network. The hospital footprint in Gràcia provides surrounding imaging, anaesthesia and surgical infrastructure for medically-involved cases.",
    labModel:
      "Hospital-group laboratory arrangements. Ceramist relationship not publicly foregrounded.",
    designWorkflow:
      "Case-dependent within the HM Dental Center protocol. Digital workflows available as a capability.",
    prepPhilosophy:
      "Institutional rather than individually authored.",
    ceramistProximity: "Not publicly named.",
    internationalHandling:
      "HM Hospitales operates structured international-patient services at group level.",
    reviewProfile:
      "Aggregated across the HM hospital estate.",
    leadsOn: [
      "Medically-integrated dental care inside HM Hospitales' Barcelona footprint",
      "Adjacent hospital infrastructure on site",
      "Coverage-and-billing integration with the main Spanish private-health insurers",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored preparation philosophy",
    ],
  },
  {
    slug: "sanitas-milenium-diagonal",
    name: "Sanitas Dental — Milenium Diagonal (Barcelona)",
    location: "Avinguda Diagonal, 495, 08029 Barcelona",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A central Barcelona location of Sanitas Dental's Milenium network — the insurance-branded flagship dental-clinic line operated by Sanitas (part of Bupa). The Diagonal clinic is one of the network's Barcelona anchors. Pricing, plan coverage and financing are structured at the national-brand level.",
    labModel:
      "Chain-standardised lab routing.",
    designWorkflow:
      "Protocolised at network level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Standard Sanitas-network intake. Primarily a domestic insurance-and-financing proposition.",
    reviewProfile:
      "Aggregated across the Sanitas-Milenium network. High volume.",
    leadsOn: [
      "Insurance-branded network location with accessible pricing and financing plans",
      "Coverage and billing integration with the Sanitas private-health ecosystem",
      "Extended-hours and weekend-emergency availability across the Milenium network",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician aesthetic authorship",
    ],
  },
  {
    slug: "vitaldent-barcelona",
    name: "Vitaldent — Barcelona (national chain)",
    location: "Multiple Vitaldent locations across Barcelona (55 clinics citywide)",
    website: "https://clinicas.vitaldent.com/barcelona",
    verified: false,
    profile:
      "A Barcelona location of Vitaldent — Spain's largest national low-cost dental chain, with more than four hundred clinics countrywide and around fifty-five across the Barcelona metropolitan area. Positioning is price-accessible with in-house financing plans.",
    labModel:
      "Chain-standardised lab arrangements.",
    designWorkflow:
      "Protocolised at chain level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic accessible-pricing proposition with standard multilingual intake.",
    reviewProfile:
      "Very high volume across the national chain.",
    leadsOn: [
      "National low-cost dental chain with in-house financing and monthly payment plans",
      "Dense Barcelona footprint with walk-in access across most districts",
      "Straightforward pricing structure for routine and restorative dentistry",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician craft positioning",
    ],
  },
];
